/**
 * Scope — the one rule the cloud shell runs on: **the sidebar always means the thing named in
 * the title bar.**
 *
 * The app is either looking at ALL SITES or at ONE site, and that choice lives in the header's
 * SiteSwitcher. Everything below it — which nav tree renders, which data a page fetches, what
 * the URL says — follows from it. Scope is never inferred from where a menu row happens to sit,
 * which is what made the old stacked-scope sidebar ambiguous.
 *
 * The scope value itself is still `NodeContext.selectedNode`: `null` IS All Sites. It used to
 * mean "nothing picked yet", which is why the per-node menu simply vanished; now it is a real
 * scope with a real menu, so there is no empty state to design.
 */

/** Query param carrying the scope, so a link opens on the site it was shared from. */
export const SCOPE_PARAM = 'site';

/**
 * Where the scope survives a reload — read by NodeContext and by the node-scoped API adapters.
 *
 * It lives here rather than in NodeContext because the auth layer has to be able to drop it on
 * logout, and AuthProvider sits ABOVE NodeProvider: importing the context from it would close the
 * two into a cycle for the sake of one `localStorage` key.
 */
export const SCOPE_STORAGE_KEY = 'trinetra-cloud-selected-node';

/**
 * Forget the stored scope, so the next mount — or the next login — starts on All Sites.
 *
 * The selected site is part of the SESSION, not of the browser: left behind, it puts the next
 * person to log in inside someone else's site, with that site's menu, for reasons they can't see.
 */
export function clearStoredScope() {
  localStorage.removeItem(SCOPE_STORAGE_KEY);
}

/** The value of `?site=` when the scope is All Sites. Also the stored sentinel — see below. */
export const ALL_SITES = 'all';

/**
 * The stored scope: a SITE id, or null for All Sites.
 *
 * Only a site is worth persisting. An earlier version stored All Sites too, as a sentinel, so
 * that deliberately choosing it would survive a reload — and that turned out to defeat the
 * whole point of a default site: the stored All Sites then out-voted the favourite on every
 * subsequent load, so the favourite applied exactly once and never again. A default site that
 * only works until you visit All Sites once is not a default.
 *
 * So All Sites is an absence again. It still holds for the rest of the session, because that
 * lives in React state; it simply does not outlast a reload, which is what "default" means.
 */
export function readStoredScope(): string | null {
  return localStorage.getItem(SCOPE_STORAGE_KEY) || null;
}

/** Record the scope. All Sites clears it — see above. */
export function storeScope(nodeId: string | null) {
  if (nodeId) localStorage.setItem(SCOPE_STORAGE_KEY, nodeId);
  else clearStoredScope();
}

/**
 * Pages that exist in BOTH scopes, so switching keeps the operator where they are.
 *
 * Only the alert board is genuinely the same surface twice today — one node's cameras vs every
 * node's. Add a pair here and the switcher starts carrying that page across scopes for free.
 */
const EQUIVALENT: Array<[site: string, allSites: string]> = [
  ['/hexagon-alerts', '/multi-site-alerts'],
  // The map is a layout of the alert board, not a page of its own, so it crosses to the
  // all-sites board rather than dead-ending on the dashboard.
  ['/map-view', '/multi-site-alerts'],
  // Both Live Walls: one site's saved grids (stored on that node) and the estate wall that can
  // mix sites (stored in the cloud). Different pages and different layout stores, but the same
  // question — "show me the cameras" — so switching scope should keep an operator on a wall
  // rather than dropping them on a dashboard.
  ['/live-wall', '/multi-site-view'],
];

/** Where each scope lands when the current page has no counterpart. */
const SITE_HOME = '/hexagon-alerts';
const ALL_SITES_HOME = '/';

/** Paths that belong to All Sites. Everything else scoped is a site's. */
const ALL_SITES_PATHS = ['/', '/multi-site-view', '/multi-site-alerts'];

/**
 * Platform administration — outside the scope system entirely. These read the cloud's own
 * database, not any node's, so switching site must leave the operator exactly where they are.
 */
const UNSCOPED_PATHS = ['/system-monitor', '/turn-monitor', '/system-alerts', '/user-management'];

export function isUnscoped(pathname: string): boolean {
  return UNSCOPED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function isAllSitesPath(pathname: string): boolean {
  return ALL_SITES_PATHS.some((p) => (p === '/' ? pathname === '/' : pathname.startsWith(p)));
}

/**
 * Where to go when the scope changes — the detail that decides whether the switcher feels
 * excellent or annoying.
 *
 *   site → site        stay put, re-scoped. An operator comparing one board across two plants
 *                      must not be thrown back to a dashboard on every switch.
 *   site → all sites   the all-sites twin if there is one, else the all-sites dashboard.
 *   all sites → site   the same mapping in reverse, else that site's alert board.
 *
 * Returns `null` to mean "don't navigate", which is the answer for a same-kind switch and for
 * every platform-admin page.
 */
export function resolveScopeSwitch(pathname: string, toAllSites: boolean): string | null {
  if (isUnscoped(pathname)) return null;

  if (toAllSites) {
    if (isAllSitesPath(pathname)) return null; // already there
    const pair = EQUIVALENT.find(([site]) => pathname === site || pathname.startsWith(`${site}/`));
    return pair ? pair[1] : ALL_SITES_HOME;
  }

  if (!isAllSitesPath(pathname)) return null; // site → site keeps the page
  const pair = EQUIVALENT.find(([, all]) => pathname === all || pathname.startsWith(`${all}/`));
  return pair ? pair[0] : SITE_HOME;
}
